import React, { Fragment, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';

import BackButton from '../../custom/BackButton';
import { renderToString } from 'react-dom/server';
import GenerateImportFlightManifest from './GenerateImportFlightManifest';
import useLoading from '../../../customHooks/useLoading';
import _ from 'lodash';
import { IFFM, IUser } from '../../../globals/interfaces';
import Card from '../../custom/Card';
import { Row } from 'reactstrap';
import { Col } from 'reactstrap';
import { Button } from 'reactstrap';
import { Table } from 'reactstrap';
import manifestColumns from './manifestColumns';
import renderRow from './renderRow';
import apiClient from '../../../apiClient';

interface ManifestData {
    totalPieces: number;
    totalWeight: number;
    totalVolume: number;
    awbs: Array<IFFM>;
}

type ManifestDataMap = Record<string, ManifestData>;

const useManifestData = (modal: boolean, s_flight_id: string) => {
    const [dataMap, setDataMap] = useState({});
    const [s_pol, set_s_pol] = useState('');
    const { setLoading } = useLoading();
    const [totalPcs, setTotalPcs] = useState(0);
    const [totalWgt, setTotalWgt] = useState(0);
    const [totalVol, setTotalVol] = useState(0);
    const [totalAwbs, setTotalAwbs] = useState(0);

    useEffect(() => {
        const getData = async (s_flight_id: string) => {
            setLoading(true);
            const res = await apiClient.post('/importManifestData', { s_flight_id });
            setLoading(false);

            if (res.status === 200) {
                const { data } = res;

                const map: ManifestDataMap = {};

                const awbsMap: Record<string, boolean> = {};
                let _totalPcs = 0, _totalWgt = 0, _totalVol = 0;

                for (let i = 0; i < data.length; i++) {
                    const current = data[i];
                    const s_uld = _.get(current, 's_uld', 'BULK');
                    _totalPcs += _.get(current, 'i_actual_piece_count', 0);
                    _totalWgt += _.get(current, 'f_weight', 0);
                    _totalVol += Number(_.get(current, 's_volume', 0));
                    awbsMap[current.s_mawb] = true;

                    if (map[s_uld] === undefined) {
                        map[s_uld] = {
                            totalPieces: current.i_actual_piece_count,
                            totalWeight: _.get(current, 'f_weight', 0),
                            totalVolume: Number(_.get(current, 's_volume', 0)),
                            awbs: [
                                current
                            ]
                        }
                    } else {
                        map[s_uld].totalPieces += current.i_actual_piece_count;
                        map[s_uld].totalWeight += current.f_weight;
                        map[s_uld].totalVolume += Number(_.get(current, 's_volume', 0));
                        map[s_uld].awbs.push(current)
                    }
                }

                setDataMap(map);
                set_s_pol(_.get(data, '[0].s_pol', null));
                setTotalPcs(_totalPcs);
                setTotalWgt(Number(_totalWgt.toFixed(1)));
                setTotalVol(Number(_totalVol.toFixed(1)));
                setTotalAwbs(Object.keys(awbsMap).length);
            }
        };

        if (s_flight_id && s_flight_id.length > 0 && modal) {
            getData(s_flight_id);
        }

    }, [s_flight_id, modal]);

    return {
        dataMap,
        s_pol,
        totalPcs,
        totalWgt,
        totalVol,
        totalAwbs
    };
}

const printManifest = (
    user: IUser,
    dataMap: ManifestDataMap,
    s_flight_id: string,
    d_arrival_date: string,
    s_pol: string,
    printConsignee: boolean,
    totalPcs: number,
    totalWgt: number,
    totalVol: number,
    totalAwbs: number
) => {
    const sheet = renderToString(
        <GenerateImportFlightManifest
            user={user}
            dataMap={dataMap}
            s_flight_id={s_flight_id}
            d_arrival_date={d_arrival_date}
            s_pol={s_pol}
            printConsignee={printConsignee}
            totalPcs={totalPcs}
            totalWgt={totalWgt}
            totalVol={totalVol}
            totalAwbs={totalAwbs}
        />
    );

    const myWindow = window.open("", "MsgWindow", "width=1920,height=1080");
    // @ts-ignore
    myWindow.document.body.innerHTML = null;
    // @ts-ignore
    myWindow.document.write(sheet);
}

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    s_flight_id: string;
    d_arrival_date: string;
    user: IUser;
}

export default function ModalFlightManifest({
    modal,
    setModal,
    s_flight_id,
    d_arrival_date,
    user
}: Props) {

    const { dataMap, s_pol, totalPcs, totalWgt, totalVol, totalAwbs } = useManifestData(modal, s_flight_id);
    const [printConsignee, setPrintConsignee] = useState(false);

    const toggle = () => setModal(!modal);

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>Import Flight Manifest</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <button
                    onClick={() => setPrintConsignee(!printConsignee)}
                    className={`btn ${printConsignee ? 'btn-success' : 'btn-grey'}`}
                >
                    Consignee
                </button>
                <Button
                    className={'float-right'}
                    onClick={() => printManifest(
                        user,
                        dataMap,
                        s_flight_id,
                        d_arrival_date,
                        s_pol,
                        printConsignee,
                        totalPcs,
                        totalWgt,
                        totalVol,
                        totalAwbs
                    )}
                >
                    Print
                </Button>
                {
                    (dataMap && Object.keys(dataMap).length > 0) &&
                    <Card>
                        <Row>
                            <Col md={12}>
                                <h6 className={'float-left'}>Preview</h6>
                            </Col>
                        </Row>
                        {
                            Object.keys(dataMap).map(key => (
                                RenderTable(dataMap, key, printConsignee)
                            ))
                        }
                    </Card>
                }
            </ModalBody>
        </CustomModal>
    );
}

const RenderTable = (dataMap: ManifestDataMap, key: string, printConsignee: boolean) => {
    return (
        <Fragment>
            <Row className={'bg-primary mx-1'}>
                <Col md={2} className={'ml-2'}>
                    <h5>{key}</h5>
                </Col>
                <Col md={6}>
                    <span>
                        Pieces: {dataMap[key].totalPieces},
                        Weight: {dataMap[key].totalWeight.toFixed(1)},
                        Volume: {dataMap[key].totalVolume.toFixed(2)}
                    </span>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Table striped>
                        <thead>
                            <tr className={'table-success'}>
                                {
                                    manifestColumns.map(c => renderRow(printConsignee, c.name) && (
                                        <th>{c.name}</th>
                                    ))
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {
                                dataMap[key].awbs.map(awb => (
                                    <tr>
                                        {
                                            manifestColumns.map(c => renderRow(printConsignee, c.name) && (
                                                <td style={{ textAlign: ['Pieces', 'Weight'].indexOf(c.name) !== -1 ? 'right' : 'left' }}>
                                                    { 
                                                        c.name === 'Routing' ?
                                                            `${awb.s_origin}/${awb.s_destination}` :
                                                        c.value === 'f_weight' ? 
                                                            awb[c.value].toFixed(2) :
                                                        c.name === 'Pieces' ?
                                                            awb.s_pieces_type === 'TOTAL_CONSIGNMENT' ?
                                                                awb.i_actual_piece_count :
                                                                `${awb.i_actual_piece_count}/${awb.i_pieces_total}` :
                                                        // @ts-ignore
                                                        awb[c.value] || ''
                                                    }
                                                </td>
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Fragment>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const CustomModal = styled(Modal)`
    max-width: 100%;
    width: 1200px;
`;